// @flow
/* eslint-env browser */ // eslint hates FormData...
import queryString from 'query-string'
import type { ActionT } from '@knowledgehound/data/Action.type'
import type { ErrorT } from '@knowledgehound/data/Error.type'
import type { StudyT } from './Study.type'
import { handleResponse, handleError } from '@knowledgehound/data/fetchUtils'
import * as requests from './StudyRequests'

// CONSTANTS
export const FETCH_STUDIES_LOADING = 'FETCH_STUDIES_LOADING'
export const FETCH_STUDIES_SUCCESS = 'FETCH_STUDIES_SUCCESS'
export const FETCH_STUDIES_ERROR = 'FETCH_STUDIES_ERROR'
export const FETCH_STUDY_LOADING = 'FETCH_STUDY_LOADING'
export const FETCH_STUDY_SUCCESS = 'FETCH_STUDY_SUCCESS'
export const FETCH_STUDY_ERROR = 'FETCH_STUDY_ERROR'
export const CREATE_STUDY_LOADING = 'CREATE_STUDY_LOADING'
export const CREATE_STUDY_SUCCESS = 'CREATE_STUDY_SUCCESS'
export const CREATE_STUDY_ERROR = 'CREATE_STUDY_ERROR'
export const DELETE_STUDY_LOADING = 'DELETE_STUDY_LOADING'
export const DELETE_STUDY_SUCCESS = 'DELETE_STUDY_SUCCESS'
export const DELETE_STUDY_ERROR = 'DELETE_STUDY_ERROR'
export const UPDATE_STUDY_LOADING = 'UPDATE_STUDY_LOADING'
export const UPDATE_STUDY_SUCCESS = 'UPDATE_STUDY_SUCCESS'
export const UPDATE_STUDY_ERROR = 'UPDATE_STUDY_ERROR'
export const UPDATE_STUDY_PHOTO_LOADING = 'UPDATE_STUDY_PHOTO_LOADING'
export const UPDATE_STUDY_PHOTO_SUCCESS = 'UPDATE_STUDY_PHOTO_SUCCESS'
export const UPDATE_STUDY_PHOTO_ERROR = 'UPDATE_STUDY_PHOTO_ERROR'

export function fetchStudiesLoading (): ActionT {
  return {
    type: FETCH_STUDIES_LOADING
  }
}

export function fetchStudiesSuccess (studies: Array<StudyT> | string): ActionT {
  return {
    type: FETCH_STUDIES_SUCCESS,
    payload: studies
  }
}

export function fetchStudiesError (err: ErrorT): ActionT {
  return {
    type: FETCH_STUDIES_ERROR,
    error: true,
    payload: err
  }
}

export function fetchStudies (params: Object = {}): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(fetchStudiesLoading())

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/?${queryString.stringify(params)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(handleResponse)
        .then((studies: Array<StudyT> | string) => {
          resolve(studies)
          dispatch(fetchStudiesSuccess(studies))
        })
        .catch(handleError(reject, dispatch, fetchStudiesError))
    })
  }
}

export function fetchStudyLoading (): ActionT {
  return {
    type: FETCH_STUDY_LOADING
  }
}

export function fetchStudySuccess (study: StudyT | string): ActionT {
  return {
    type: FETCH_STUDY_SUCCESS,
    payload: study
  }
}

export function fetchStudyError (err: ErrorT): ActionT {
  return {
    type: FETCH_STUDY_ERROR,
    error: true,
    payload: err
  }
}

export function fetchStudy (studyNumber: string): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(fetchStudyLoading())

    return new Promise((resolve: Function, reject: Function) => {
      requests.getStudy(studyNumber)
        .then(function (study: StudyT | string) {
          resolve(study)
          dispatch(fetchStudySuccess(study))
        })
        .catch(handleError(reject, dispatch, fetchStudyError))
    })
  }
}

export function createStudyLoading (): ActionT {
  return {
    type: CREATE_STUDY_LOADING
  }
}

export function createStudySuccess (study: StudyT | string): ActionT {
  return {
    type: CREATE_STUDY_SUCCESS,
    payload: study
  }
}

export function createStudyError (err: ErrorT): ActionT {
  return {
    type: CREATE_STUDY_ERROR,
    error: true,
    payload: err
  }
}

export function createStudy (study: StudyT): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(createStudyLoading())

    return new Promise((resolve: Function, reject: Function) => {
      fetch('/proxy/greyhound/studies/', {
        method: 'POST',
        body: JSON.stringify(study),
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(handleResponse)
        .then(function (study: StudyT | string) {
          resolve(study)
          dispatch(createStudySuccess(study))
        })
        .catch(handleError(reject, dispatch, createStudyError))
    })
  }
}

export function updateStudyLoading (): ActionT {
  return {
    type: UPDATE_STUDY_LOADING
  }
}

export function updateStudySuccess (study: StudyT | string): ActionT {
  return {
    type: UPDATE_STUDY_SUCCESS,
    payload: study
  }
}

export function updateStudyError (err: ErrorT): ActionT {
  return {
    type: UPDATE_STUDY_ERROR,
    error: true,
    payload: err
  }
}

export function updateStudy (study: Object): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(updateStudyLoading())

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/${study.id}/`, {
        method: 'PATCH',
        body: JSON.stringify(study),
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(handleResponse)
        .then(function (study: StudyT | string) {
          resolve(study)
          dispatch(updateStudySuccess(study))
        })
        .catch(handleError(reject, dispatch, updateStudyError))
    })
  }
}

export function deleteStudyLoading (studyId: string): ActionT {
  return {
    type: DELETE_STUDY_LOADING,
    payload: {
      id: studyId
    }
  }
}

export function deleteStudySuccess (id: string): ActionT {
  return {
    type: DELETE_STUDY_SUCCESS,
    payload: { id }
  }
}

export function deleteStudyError (err: ErrorT): ActionT {
  return {
    type: DELETE_STUDY_ERROR,
    error: true,
    payload: err
  }
}

export function deleteStudy (studyId: string): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(deleteStudyLoading(studyId))

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/${studyId}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      })
        .then(handleResponse)
        .then(function () {
          resolve()
          dispatch(deleteStudySuccess(studyId))
        })
        .catch(handleError(reject, dispatch, deleteStudyError))
    })
  }
}

export function requestDeleteStudyLoading (studyId: string): ActionT {
  return {
    type: 'REQUEST_DELETE_STUDY_LOADING',
    payload: {
      id: studyId
    }
  }
}

export function requestDeleteStudySuccess (id: string, comment: null | string): ActionT {
  return {
    type: 'REQUEST_DELETE_STUDY_SUCCESS',
    payload: { id, comment }
  }
}

export function requestDeleteStudyError (err: ErrorT): ActionT {
  return {
    type: 'REQUEST_DELETE_STUDY_ERROR',
    error: true,
    payload: err
  }
}

export function requestDeleteStudy (studyId: string, comment: null | string): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(requestDeleteStudyLoading(studyId))

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/${studyId}/deletion-requests/`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({comment})
      })
        .then(handleResponse)
        .then(() => {
          resolve()
          dispatch(requestDeleteStudySuccess(studyId, comment))
        })
        .catch(handleError(reject, dispatch, requestDeleteStudyError))
    })
  }
}

export function clearStudyDeletionRequestsLoading (studyId: string): ActionT {
  return {
    type: 'CLEAR_STUDY_DELETION_REQUESTS_LOADING',
    payload: { id: studyId }
  }
}

export function clearStudyDeletionRequestsSuccess (studyId: string): ActionT {
  return {
    type: 'CLEAR_STUDY_DELETION_REQUESTS_SUCCESS',
    payload: { id: studyId }
  }
}

export function clearStudyDeletionRequestsError (err: ErrorT): ActionT {
  return {
    type: 'CLEAR_STUDY_DELETION_REQUESTS_ERROR',
    error: true,
    payload: err
  }
}

export function clearStudyDeletionRequests (studyId: string, comment: null | string): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(clearStudyDeletionRequestsLoading(studyId))

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/${studyId}/deletion-requests/clear/`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({comment})
      })
        .then(handleResponse)
        .then(() => {
          resolve()
          dispatch(clearStudyDeletionRequestsSuccess(studyId))
        })
        .catch(handleError(reject, dispatch, clearStudyDeletionRequestsError))
    })
  }
}

export function updateStudyPhotoLoading (): ActionT {
  return {
    type: UPDATE_STUDY_PHOTO_LOADING
  }
}

export function updateStudyPhotoSuccess (study: StudyT | string): ActionT {
  return {
    type: UPDATE_STUDY_PHOTO_SUCCESS,
    payload: study
  }
}

export function updateStudyPhotoError (err: ErrorT): ActionT {
  return {
    type: UPDATE_STUDY_PHOTO_ERROR,
    error: true,
    payload: err
  }
}

export function updateStudyPhoto (study: Object, file: File): Function {
  return (dispatch: Function, getState: Function) => {
    dispatch(updateStudyPhotoLoading())
    const formData = new FormData()
    formData.append('cover_photo', file)
    formData.append('_method', 'PATCH') // Damn PHP

    return new Promise((resolve: Function, reject: Function) => {
      fetch(`/proxy/greyhound/studies/${study.id}/`, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin'
      })
        .then(handleResponse)
        .then(function (updatedStudy: StudyT | string) {
          resolve(updatedStudy)
          dispatch(updateStudyPhotoSuccess(updatedStudy))
        })
        .catch(handleError(reject, dispatch, updateStudyPhotoError))
    })
  }
}
